// Written at build time by scripts/fetch-content.mjs: published CMS results keyed by
// query + params. Prerendered pages and the first paint use it, so visitors and search
// engines get real content immediately. Gitignored; written as {} if Sanity is unreachable.
import snapshot from "../content-snapshot.json";

// The project ID and dataset are public identifiers (they appear in every browser request),
// so they have safe defaults here and no longer depend on a host-specific .env file.
export const SANITY_PROJECT_ID = process.env.REACT_APP_SANITY_PROJECT_ID || "nfsxzaf2";
export const SANITY_DATASET = process.env.REACT_APP_SANITY_DATASET || "production";
export const SANITY_API_VERSION = process.env.REACT_APP_SANITY_API_VERSION || "2026-08-07";

// Minimal client for the Sanity GROQ HTTP API (published content via the CDN). Replaces
// @sanity/client (~150 KB with dependencies); most queries are served from the snapshot.
const API_BASE = `https://${SANITY_PROJECT_ID}.apicdn.sanity.io/v${SANITY_API_VERSION}/data/query/${SANITY_DATASET}`;

async function fetchFromSanity(query, params = {}) {
    const qs = new URLSearchParams({ query, perspective: "published" });
    for (const [key, value] of Object.entries(params)) qs.set(`$${key}`, JSON.stringify(value));
    const res = await fetch(`${API_BASE}?${qs}`);
    if (!res.ok) throw new Error(`Sanity query failed: HTTP ${res.status}`);
    const { result } = await res.json();
    return result ?? null;
}

export const snapshotKey = (query, params = {}) => JSON.stringify([query, params]);

const lookup = (query, params) => {
    const key = snapshotKey(query, params);
    return Object.prototype.hasOwnProperty.call(snapshot, key) ? snapshot[key] : undefined;
};

let peeking = false;

// Every query goes through here. A snapshot hit resolves without a network request;
// anything not in the snapshot (for example a service added since the last build)
// falls back to a live request.
export function fetchQuery(query, params = {}) {
    const hit = lookup(query, params);
    if (peeking) return hit === undefined ? null : hit;
    if (hit !== undefined) return Promise.resolve(hit);
    return fetchFromSanity(query, params);
}

// Synchronously read a query's snapshot result (or null) for use as initial state,
// e.g. useState(() => peek(getHomepageHero)). Keeps the first render identical to the
// prerendered HTML so content does not flash or shift when JavaScript loads.
export function peek(getter, ...args) {
    peeking = true;
    try {
        const value = getter(...args);
        return value && typeof value.then === "function" ? null : value ?? null;
    } finally {
        peeking = false;
    }
}
